import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaCalendarAlt, FaTimes } from "react-icons/fa";
import {
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const EmailList = () => {
    // Date filter states
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Custom styles
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "50px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    // Table columns
    const columns = [
        {
            name: "To",
            selector: (row) => row.to_address,
            sortable: true,
            width: "13%",
        },
        {
            name: "Subject",
            selector: (row) => row.subject,
            sortable: true,
            width: "19%",
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            width: "9%",
            cell: (row) => (
                <span
                    style={{
                        color:
                            row.status === "sent"
                                ? "green"
                                : row.status === "failed"
                                  ? "red"
                                  : row.status === "processing"
                                    ? "orange"
                                    : "#007bff",
                        fontWeight: "bold",
                    }}
                >
                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                </span>
            ),
        },
        {
            name: "Reason",
            selector: (row) => row.error,
            sortable: true,
            width: "22%",
        },
        {
            name: "Created At",
            selector: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
            width: "18%",
        },
        {
            name: "Sent At",
            selector: (row) =>
                row.sent_at ? new Date(row.sent_at).toLocaleString() : "N/A",
            sortable: true,
            width: "18%",
        },
    ];

    // Fetch emails
    useEffect(() => {
        const fetchEmails = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/emails`,
                    {
                        withCredentials: true,
                    },
                );
                // Ensure rendered_content is present for each email (for backward compatibility)
                const emailsWithRendered = response.data.map((email) => ({
                    ...email,
                    rendered_content:
                        email.rendered_content || email.body || "",
                }));
                setEmails(sortByAuditUpdatedOn(emailsWithRendered));
                setLoading(false);
            } catch(error) {
                console.error("Error fetching emails:", error);
                setLoading(false);
            }
        };
        fetchEmails();
    }, []);

    // Filter emails
    const filteredEmails = emails.filter((email) => {
        // Text search filter
        const matchesSearch =
            email.to_address.toLowerCase().includes(search.toLowerCase()) ||
            email.subject.toLowerCase().includes(search.toLowerCase()) ||
            (email.status &&
                email.status.toLowerCase().includes(search.toLowerCase()));

        // Status filter
        const matchesStatus =
            statusFilter === "all" ||
            (email.status &&
                email.status.toLowerCase() === statusFilter.toLowerCase());

        // Date range filter (created_at)
        let matchesDate = true;
        if(startDate || endDate) {
            const createdDate = new Date(email.created_at);
            if(startDate) {
                const startOfDay = new Date(startDate);
                startOfDay.setHours(0, 0, 0, 0);
                if(createdDate < startOfDay) {
                    matchesDate = false;
                }
            }
            if(endDate && matchesDate) {
                const endOfDay = new Date(endDate);
                endOfDay.setHours(23, 59, 59, 999);
                if(createdDate > endOfDay) {
                    matchesDate = false;
                }
            }
        }
        return matchesSearch && matchesStatus && matchesDate;
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredEmails.length / perPage);
    const paginatedData = filteredEmails.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, filteredEmails.length);
    const totalRecords = filteredEmails.length;

    // Export CSV (uses filtered, unpaginated data)
    const handleExportEmailsCSV = () => {
        try {
            if (!filteredEmails.length) {
                notifyUser(null, "info", "No records to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = filteredEmails.map((e) => ({
                To: e.to_address || "",
                Subject: e.subject || "",
                Status: e.status || "",
                Reason: e.error || "",
                "Created At": e.created_at ? new Date(e.created_at).toLocaleString() : "",
                "Sent At": e.sent_at ? new Date(e.sent_at).toLocaleString() : "",
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            const now = new Date();
            const filename = `email_queue_${now.toISOString().split("T")[0]}.csv`;
            link.setAttribute("href", url);
            link.setAttribute("download", filename);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Error exporting emails:", err);
            notifyUser(null, "error", "Failed to export emails", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    // Custom Pagination Component
    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {filteredEmails.length === 0 ? 0 : fromRecord} to{" "}
                {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    // Modal handlers
    const handleRowClick = (row) => {
        setSelectedEmail(row);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedEmail(null);
    };

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                EMAIL QUEUE
            </h2>

            {/* Search and Pagination Controls */}
            <div
                className="aqi-grid-toolbar"
                style={{
                flexWrap: "nowrap",
                gap: "8px",
                overflowX: "auto",
                }}
            >
                {/* Rows per page - Top Left */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>
                {/* Date Range Filter - styled like secureLog.js */}
                <div className="flex gap-2 items-center flex-nowrap">
                    <div className="relative">
                        <div
                            className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all"
                        >
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => {
                                    setStartDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsStart
                                startDate={startDate}
                                endDate={endDate}
                                placeholderText="Start Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1"
                                style={{
                                    width: "clamp(70px, 8vw, 112px)",
                                }}
                            />
                            {startDate && (
                                <button
                                    onClick={() => setStartDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                    <span className="text-navy">to</span>
                    <div className="relative">
                        <div
                            className="aqi-grid-control flex items-center border-2 border-[#C2D7E2] rounded-md bg-[#F5F7FA] px-2 cursor-pointer text-navy hover:border-navy focus-within:border-navy focus-within:ring-1 focus-within:ring-navy focus-within:shadow-sm transition-all"
                        >
                            <FaCalendarAlt className="text-navy mr-2" />
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => {
                                    setEndDate(date);
                                    setCurrentPage(1);
                                }}
                                selectsEnd
                                startDate={startDate}
                                endDate={endDate}
                                minDate={startDate}
                                placeholderText="End Date"
                                dateFormat="MM/dd/yyyy"
                                className="bg-transparent border-0 outline-none text-sm p-1"
                                style={{
                                    width: "clamp(70px, 8vw, 112px)",
                                }}
                            />
                            {endDate && (
                                <button
                                    onClick={() => setEndDate(null)}
                                    className="text-gray-400 hover:text-red-500"
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "0 4px",
                                    }}
                                >
                                    <FaTimes size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status Filter */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                            width: "clamp(105px, 11vw, 160px)",
                            minWidth: "100px",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="all">All Statuses</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                        <option value="processing">Processing</option>
                        <option value="pending">Pending</option>
                    </select>
                </div>

                {/* Search bar - Top Right */}
                <div
                    className="aqi-grid-search"
                    style={{
                        width: "clamp(130px, 16vw, 250px)",
                        minWidth: "120px",
                        flexShrink: 1,
                    }}
                >
                    <FaSearch
                        className="absolute left-3 top-2.5"
                        style={{ color: "var(--navy)" }}
                    />
                    <input
                        type="text"
                        placeholder="Search emails..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                        style={{
                            paddingLeft: "2.5rem",
                            paddingRight: "0.75rem",
                            paddingTop: "0.5rem",
                            paddingBottom: "0.5rem",
                            width: "100%",
                            borderRadius: "0.375rem",
                            border: "2px solid #C2D7E2",
                            backgroundColor: "#F5F7FA",
                            transition: "all 0.2s ease",
                            outline: "none",
                            color: "var(--navy)",
                        }}
                        className="aqi-grid-search-input focus:border-navy focus:ring-navy focus:ring-1 focus:shadow-sm hover:border-navy"
                    />
                </div>
                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportEmailsCSV}
                        disabled={totalRecords === 0}
                        className={`aqi-grid-button aqi-grid-button--primary ${
                            totalRecords === 0 ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                progressPending={loading}
                pagination={false}
                onRowClicked={handleRowClick}
                highlightOnHover
                pointerOnHover
                responsive
                striped
            />

            {/* Custom Pagination UI */}
            <CustomPagination />

            {/* Email Detail Modal */}
            {showModal && selectedEmail && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "8px",
                            width: "80%",
                            maxWidth: "800px",
                            maxHeight: "90vh",
                            overflow: "auto",
                            boxShadow: "0 0 30px rgba(0,0,0,0.2)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "20px",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "10px",
                            }}
                        >
                            <h2 style={{ color: "#007bff", margin: 0 }}>
                                Email Details
                            </h2>
                            <button
                                onClick={closeModal}
                                style={{
                                    background: "#007bff",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    padding: "8px 15px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                }}
                            >
                                Close
                            </button>
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    To:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedEmail.to_address}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Subject:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedEmail.subject}
                                </p>
                            </div>
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Status:
                                </h3>
                                <p
                                    style={{
                                        margin: 0,
                                        color:
                                            selectedEmail.status === "sent"
                                                ? "green"
                                                : selectedEmail.status ===
                                                    "failed"
                                                  ? "red"
                                                  : "#007bff",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {selectedEmail.status
                                        .charAt(0)
                                        .toUpperCase() +
                                        selectedEmail.status.slice(1)}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Job ID:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedEmail.job_id || "N/A"}
                                </p>
                            </div>
                        </div>

                        {selectedEmail.error && (
                            <div style={{ marginBottom: "20px" }}>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Error Details:
                                </h3>
                                <div
                                    style={{
                                        backgroundColor: "#F5F7FA",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        borderLeft: "4px solid #dc3545",
                                    }}
                                >
                                    <pre
                                        style={{
                                            margin: 0,
                                            whiteSpace: "pre-wrap",
                                            fontFamily: "monospace",
                                            fontSize: "13px",
                                        }}
                                    >
                                        {selectedEmail.error}
                                    </pre>
                                </div>
                            </div>
                        )}

                        <div style={{ marginBottom: "20px" }}>
                            <h3 style={{ color: "#555", marginBottom: "5px" }}>
                                Content:
                            </h3>
                            <div
                                style={{
                                    backgroundColor: "#F5F7FA",
                                    padding: "15px",
                                    borderRadius: "4px",
                                    minHeight: "100px",
                                    overflowX: "auto",
                                }}
                            >
                                {selectedEmail.rendered_content ? (
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: selectedEmail.rendered_content,
                                        }}
                                    />
                                ) : (
                                    <p style={{ margin: 0, color: "#6c757d" }}>
                                        No rendered content available
                                    </p>
                                )}
                            </div>
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                            }}
                        >
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Created At:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {new Date(
                                        selectedEmail.created_at,
                                    ).toLocaleString()}
                                </p>
                            </div>
                            <div>
                                <h3
                                    style={{
                                        color: "#555",
                                        marginBottom: "5px",
                                    }}
                                >
                                    Sent At:
                                </h3>
                                <p style={{ margin: 0 }}>
                                    {selectedEmail.sent_at
                                        ? new Date(
                                              selectedEmail.sent_at,
                                          ).toLocaleString()
                                        : "Not sent"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailList;
